import { useTranslation } from 'react-i18next';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import { AnchorPicker } from '../AnchorPicker';
import { NewExtensionFormComponent, NewExtensionFormComponentRef, NewExtensionFormValues, LabelType, SummandFormValue } from './NewExtensionForm';
import { useCreateExtension, UUID, useTaxonomyLanguages, CreateExtensionSummand, useConcept } from 'entities/tag';
import { FieldPath } from 'react-hook-form';
import { api } from 'shared/api/client';

import './NewExtensionModal.scss';
import { Typography } from 'app/components/ui';

interface NewExtensionModalProps {
  show: boolean;
  onClose: () => void;
  documentSuperId: number;
  taxonomyId: UUID;
  widerAnchorSeed?: string | null;
}

export function cleanLabels(labels: NewExtensionFormValues['labels']): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const lang of Object.keys(labels)) {
    const byType = labels[lang] ?? {};
    for (const type of Object.keys(byType) as LabelType[]) {
      const text = byType[type];
      if (typeof text === 'string' && text.trim().length > 0) {
        if (!out[lang]) out[lang] = {};
        out[lang][type] = text.trim();
      }
    }
  }
  return out;
}

async function resolveSummands(summands: SummandFormValue[], documentSuperId: number): Promise<CreateExtensionSummand[]> {
  if (summands.length === 0) return [];
  const resolved = await Promise.all(
    summands.map(async (s) => {
      const { data: concept } = await api.GET('/taxonomy/concepts', {
        params: { query: { name: s.conceptName, documentSuperId: String(documentSuperId) } },
      });
      if (!concept?.id) throw new Error(`Could not resolve summand concept: ${s.conceptName}`);
      const dimQualifiers = (s.dimQualifiers ?? []).filter((q) => !!q.axisConceptId && !!q.memberConceptId);
      return { conceptId: concept.id, weight: s.weight, dimQualifiers };
    })
  );
  return resolved;
}

export const NewExtensionModal = ({ show, onClose, documentSuperId, taxonomyId, widerAnchorSeed }: NewExtensionModalProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const [view, setView] = useState<'form' | 'anchorPicker'>('form');
  const [anchorType, setAnchorType] = useState<'wider' | 'narrower' | 'summand' | null>(null);
  const [anchorSelected, setAnchorSelected] = useState<string | null>(null);
  const [summandPickIndex, setSummandPickIndex] = useState<number | null>(null);
  const [widerAnchorName, setWiderAnchorName] = useState<string | null>(widerAnchorSeed ?? null);
  const [narrowerAnchorName, setNarrowerAnchorName] = useState<string | null>(null);
  const formRef = useRef<NewExtensionFormComponentRef>(null);

  useEffect(() => {
    if (show) {
      setView('form');
      setWiderAnchorName(widerAnchorSeed ?? null);
      setNarrowerAnchorName(null);
    }
  }, [show, widerAnchorSeed]);

  const createExtension = useCreateExtension();
  const { data: languages } = useTaxonomyLanguages(documentSuperId, taxonomyId);

  const { data: widerAnchorConcept } = useConcept(widerAnchorName, documentSuperId);
  const { data: narrowerAnchorConcept } = useConcept(narrowerAnchorName, documentSuperId);

  const openAnchorPicker = (type: 'wider' | 'narrower' | 'summand', selected: string | null, summandIndex?: number) => {
    setAnchorType(type);
    setAnchorSelected(selected);
    setSummandPickIndex(typeof summandIndex === 'number' ? summandIndex : null);
    setView('anchorPicker');
  };

  const handleAnchorPicked = (conceptName: string) => {
    if (anchorType === 'wider') {
      setWiderAnchorName(conceptName);
      formRef.current?.setValue('widerAnchor', conceptName);
    } else if (anchorType === 'narrower') {
      setNarrowerAnchorName(conceptName);
      formRef.current?.setValue('narrowerAnchors', conceptName);
    } else if (anchorType === 'summand' && summandPickIndex !== null) {
      formRef.current?.setValue(`summands.${summandPickIndex}.conceptName` as FieldPath<NewExtensionFormValues>, conceptName);
    }
    setView('form');
  };

  const handleSubmit = async (data: NewExtensionFormValues) => {
    if (!widerAnchorConcept?.id) {
      return;
    }
    const narrowerAnchorConceptIds = narrowerAnchorConcept?.id ? [narrowerAnchorConcept.id] : [];

    const balance: 'debit' | 'credit' | null = data.balanceType === 'debit' || data.balanceType === 'credit' ? data.balanceType : null;
    const periodType: 'instant' | 'duration' = data.periodType === 'instant' ? 'instant' : 'duration';

    const summands = data.kind === 'SUB_TOTAL' ? await resolveSummands(data.summands ?? [], documentSuperId) : undefined;

    await createExtension.mutateAsync({
      documentSuperId,
      taxonomyId,
      name: data.concept,
      description: data.description ?? null,
      labels: cleanLabels(data.labels),
      widerAnchorConceptId: widerAnchorConcept.id,
      narrowerAnchorConceptIds,
      conceptType: { baseXbrliType: data.dataType, balance, periodType },
      kind: data.kind,
      summands,
    });
    onClose();
  };

  const readyLanguages = languages && languages.length > 0 ? languages : undefined;
  const formKey = `${show ? 'open' : 'closed'}-${widerAnchorSeed ?? ''}-${(languages ?? []).join(',')}`;

  return (
    <BootstrapModal show={show} onHide={onClose} contentClassName="new-extension__modal" centered>
      <BootstrapModal.Header className="new-extension__modal--header">
        <BootstrapModal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {t('tag:extension.create.new')}
          </Typography>
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="new-extension__modal--body">
        <div style={{ display: view === 'form' ? 'block' : 'none' }}>
          {readyLanguages && (
            <NewExtensionFormComponent
              key={formKey}
              ref={formRef}
              languages={readyLanguages}
              onOpenAnchorPicker={openAnchorPicker}
              onCancel={onClose}
              onSubmit={handleSubmit}
              widerAnchorSeed={widerAnchorSeed ?? null}
              documentSuperId={documentSuperId}
            />
          )}
        </div>

        {view === 'anchorPicker' && anchorType && (
          <AnchorPicker
            anchorKind={anchorType === 'summand' ? 'narrower' : anchorType}
            documentSuperId={documentSuperId}
            onSelect={({ conceptName }) => handleAnchorPicked(conceptName)}
            onClose={() => setView('form')}
            selected={anchorSelected}
          />
        )}
      </BootstrapModal.Body>
      {view === 'form' && (
        <BootstrapModal.Footer className="new-extension__modal--footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common:cancel')}
          </button>
          <button type="submit" form="new-extension-form" className="btn btn-primary">
            {t('common:apply')}
          </button>
        </BootstrapModal.Footer>
      )}
    </BootstrapModal>
  );
};
